import { Check, Clock, DollarSign } from "lucide-react";
import type { SowiftProjectResponse } from "@/types";
import type { TabId } from "./constants";

export function OverviewTab({
  project,
  formatCurrency,
  onTabChange,
}: Readonly<{
  project: SowiftProjectResponse;
  formatCurrency: (v: number | null | undefined, c: string) => string;
  onTabChange: (tab: TabId) => void;
}>) {
  const statusSteps: {
    key: string;
    label: string;
    tab?: TabId;
  }[] = [
    { key: "draft", label: "Draft", tab: "overview" },
    { key: "gathering_context", label: "Context", tab: "context" },
    { key: "analyzing", label: "Analysis", tab: "analysis" },
    { key: "generating", label: "Generating", tab: "documents" },
    { key: "review", label: "Review", tab: "documents" },
    { key: "complete", label: "Complete", tab: "documents" },
  ];

  const currentIdx = statusSteps.findIndex((s) => s.key === project.status);

  return (
    <div className="max-w-3xl space-y-6">
      {/* Workflow Progress */}
      <div>
        <h3 className="text-sm font-medium text-content-tertiary mb-3">Workflow Progress</h3>
        <div className="bg-surface-primary border border-border-primary rounded-xl p-5">
          <div className="flex items-center justify-between">
            {statusSteps.map((step, idx) => {
              const isCompleted = idx < currentIdx;
              const isCurrent = idx === currentIdx;

              return (
                <div key={step.key} className="flex items-center flex-1 last:flex-none">
                  {/* Step circle + label */}
                  <button
                    onClick={() => step.tab && onTabChange(step.tab)}
                    className="flex flex-col items-center gap-1.5 group"
                    title={`Go to ${step.label}`}
                  >
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${(() => {
                        if (isCompleted) return "bg-green-500/20 text-green-400 border-2 border-green-500/40";
                        if (isCurrent)
                          return "bg-brand-primary-subtle text-brand-primary border-2 border-brand-primary/60 ring-2 ring-brand-primary/20";
                        return "bg-surface-secondary text-content-quaternary border-2 border-border-secondary";
                      })()} ${step.tab ? "group-hover:border-brand-primary/40 cursor-pointer" : ""}`}
                    >
                      {isCompleted ? <Check className="h-4 w-4" /> : idx + 1}
                    </div>
                    <span
                      className={`text-xs font-medium ${(() => {
                        if (isCompleted) return "text-green-400";
                        if (isCurrent) return "text-brand-primary";
                        return "text-content-quaternary";
                      })()}`}
                    >
                      {step.label}
                    </span>
                  </button>

                  {/* Connector line */}
                  {idx < statusSteps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 mt-[-18px] ${
                        idx < currentIdx ? "bg-green-500/40" : "bg-surface-secondary"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Description */}
      {project.description && (
        <div>
          <h3 className="text-sm font-medium text-content-tertiary mb-2">Description</h3>
          <p className="text-content-primary">{project.description}</p>
        </div>
      )}

      {/* Financial Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <div className="flex items-center gap-2 text-content-tertiary text-xs font-medium mb-1">
            <DollarSign className="h-3.5 w-3.5" />
            Client Budget
          </div>
          <p className="text-lg font-semibold text-content-primary">
            {formatCurrency(project.clientBudget, project.currency)}
          </p>
        </div>
        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <div className="flex items-center gap-2 text-content-tertiary text-xs font-medium mb-1">
            <DollarSign className="h-3.5 w-3.5" />
            ROM Range
          </div>
          <p className="text-lg font-semibold text-content-primary">
            {project.romLow != null || project.romHigh != null
              ? `${formatCurrency(project.romLow, project.currency)} - ${formatCurrency(project.romHigh, project.currency)}`
              : "---"}
          </p>
        </div>
        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <div className="flex items-center gap-2 text-content-tertiary text-xs font-medium mb-1">
            <Clock className="h-3.5 w-3.5" />
            Last Updated
          </div>
          <p className="text-lg font-semibold text-content-primary">
            {new Date(project.updatedAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <div className="text-content-tertiary text-xs font-medium mb-1">Context Items</div>
          <p className="text-2xl font-semibold text-content-primary">{project.contextItems.length}</p>
        </div>
        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <div className="text-content-tertiary text-xs font-medium mb-1">Documents</div>
          <p className="text-2xl font-semibold text-content-primary">{project.documents.length}</p>
        </div>
      </div>
    </div>
  );
}
